import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { getSupportErrorCode, SupportApi } from '@api/SupportApi';

export const MAX_ATTACHMENT_COUNT = 20;
export const MAX_ATTACHMENT_SIZE_BYTES = 20 * 1024 * 1024;
const UPLOAD_LIFETIME_BEFORE_REFRESH_MS = 50 * 60 * 1000;

export type SupportAttachmentError = 'tooLarge' | 'uploadFailed' | 'invalid';

export type ComposerAttachmentState =
    | { kind: 'uploading'; progress: number }
    | { kind: 'uploaded'; attachmentId: string; uploadedAt: number }
    | { kind: 'failed'; error: SupportAttachmentError };

export interface ComposerAttachment {
    localId: string;
    file: File;
    state: ComposerAttachmentState;
}

export interface ComposerAttachments {
    attachments: ComposerAttachment[];
    isLimitExceeded: boolean;
    isUploading: boolean;
    hasFailedAttachments: boolean;
    attachFiles(files: File[]): void;
    removeAttachment(localId: string): void;
    retryAttachment(localId: string): void;
    prepareForSending(): Promise<ComposerAttachment[] | undefined>;
    clearAttachments(): void;
    restoreRejectedAttachments(attachments: ComposerAttachment[]): void;
}

export function useComposerAttachments(): ComposerAttachments {
    const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);
    const [isLimitExceeded, setIsLimitExceeded] = useState(false);
    const uploadControllers = useRef(new Map<string, AbortController>());

    useEffect(() => {
        const controllers = uploadControllers.current;
        return () => controllers.forEach(controller => controller.abort());
    }, []);

    const setAttachmentState = (localId: string, state: ComposerAttachmentState): void => {
        setAttachments(current =>
            current.map(attachment => (attachment.localId === localId ? { ...attachment, state } : attachment)),
        );
    };

    const upload = async (attachment: ComposerAttachment): Promise<ComposerAttachment> => {
        uploadControllers.current.get(attachment.localId)?.abort();
        const controller = new AbortController();
        uploadControllers.current.set(attachment.localId, controller);
        setAttachmentState(attachment.localId, { kind: 'uploading', progress: 0 });
        let state: ComposerAttachmentState;
        try {
            const { attachmentId } = await SupportApi.uploadAttachment(attachment.file, {
                signal: controller.signal,
                onProgress: progress => setAttachmentState(attachment.localId, { kind: 'uploading', progress }),
            });
            state = { kind: 'uploaded', attachmentId, uploadedAt: Date.now() };
        } catch (error) {
            state = { kind: 'failed', error: isPayloadTooLarge(error) ? 'tooLarge' : 'uploadFailed' };
            if (axios.isCancel(error)) return { ...attachment, state };
        } finally {
            if (uploadControllers.current.get(attachment.localId) === controller) {
                uploadControllers.current.delete(attachment.localId);
            }
        }
        setAttachmentState(attachment.localId, state);

        return { ...attachment, state };
    };

    const attachFiles = (files: File[]): void => {
        const freeSlots = Math.max(MAX_ATTACHMENT_COUNT - attachments.length, 0);
        const attachedFiles = files.slice(0, freeSlots).map(createAttachment);
        setIsLimitExceeded(files.length > attachedFiles.length);
        setAttachments(current => [...current, ...attachedFiles]);
        attachedFiles
            .filter(attachment => attachment.state.kind === 'uploading')
            .forEach(attachment => void upload(attachment));
    };

    return {
        attachments,
        isLimitExceeded,
        isUploading: attachments.some(attachment => attachment.state.kind === 'uploading'),
        hasFailedAttachments: attachments.some(attachment => attachment.state.kind === 'failed'),
        attachFiles,
        removeAttachment: localId => {
            uploadControllers.current.get(localId)?.abort();
            uploadControllers.current.delete(localId);
            setAttachments(current => current.filter(attachment => attachment.localId !== localId));
            setIsLimitExceeded(false);
        },
        retryAttachment: localId => {
            const attachment = attachments.find(candidate => candidate.localId === localId);
            if (attachment && !isTooLarge(attachment.file)) void upload(attachment);
        },
        prepareForSending: async () => {
            const preparedAttachments = await Promise.all(
                attachments.map(attachment => (isUploadExpiring(attachment) ? upload(attachment) : attachment)),
            );

            return preparedAttachments.every(attachment => attachment.state.kind === 'uploaded')
                ? preparedAttachments
                : undefined;
        },
        clearAttachments: () => {
            setAttachments([]);
            setIsLimitExceeded(false);
        },
        restoreRejectedAttachments: rejectedAttachments => {
            setAttachments(
                rejectedAttachments.map(attachment => ({ ...attachment, state: { kind: 'failed', error: 'invalid' } })),
            );
        },
    };
}

function createAttachment(file: File): ComposerAttachment {
    return {
        localId: uuid(),
        file,
        state: isTooLarge(file) ? { kind: 'failed', error: 'tooLarge' } : { kind: 'uploading', progress: 0 },
    };
}

function isTooLarge(file: File): boolean {
    return file.size > MAX_ATTACHMENT_SIZE_BYTES;
}

function isUploadExpiring({ state }: ComposerAttachment): boolean {
    return state.kind === 'uploaded' && Date.now() - state.uploadedAt > UPLOAD_LIFETIME_BEFORE_REFRESH_MS;
}

function isPayloadTooLarge(error: unknown): boolean {
    return (
        (axios.isAxiosError(error) && error.response?.status === 413) ||
        getSupportErrorCode(error) === 'PAYLOAD_TOO_LARGE'
    );
}
